import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Portal } from '../common/Portal';
import { ThreadTree } from './ThreadTree';
import { useThreads } from '../../hooks/useThreads';

interface MessageThreadModalProps {
  isOpen: boolean;
  threadId: string | null;
  onClose: () => void;
  currentUserId?: string;
  isAdmin: boolean;
  onAddComment: (messageId: string, content: string) => Promise<boolean>;
}

export function MessageThreadModal({
  isOpen,
  threadId,
  onClose,
  currentUserId,
  isAdmin,
  onAddComment,
}: MessageThreadModalProps) {
  const [booting, setBooting] = useState(true);
  const {
    threadedMessages,
    loading,
    createReply,
    editMessage,
    toggleLike,
    deleteMessage,
  } = useThreads(threadId || undefined);

  // Adapt createReply to expected void return
  const handleReply = async (content: string, parentId: string) => {
    await createReply(content, parentId);
  };

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Focus management
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (isOpen) {
      // Defer focus to ensure element exists
      const t = setTimeout(() => closeBtnRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Show a spinner immediately for a short time to avoid initial content flash
  useEffect(() => {
    if (!isOpen || !threadId) return;
    setBooting(true);
    const t = setTimeout(() => setBooting(false), 200);
    return () => clearTimeout(t);
  }, [isOpen, threadId]);

  // Ensure next open starts with spinner immediately
  useEffect(() => {
    if (!isOpen) {
      setBooting(true);
    }
  }, [isOpen]);

  if (!isOpen || !threadId) return null;

  // Determine readiness to render the thread content
  const hasMessages = Array.isArray(threadedMessages) && threadedMessages.length > 0;
  const ready = !booting && !loading && hasMessages;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-x-hidden"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className="relative inline-block w-full sm:w-auto max-w-full sm:max-w-4xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button outside the container (top-right) */}
          <button
            onClick={onClose}
            className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-[1] p-1.5 rounded-full bg-black/60 text-white hover:bg-black/70 border border-white/10 shadow"
            aria-label="Close thread"
            ref={closeBtnRef}
          >
            <X className="w-4 h-4" />
          </button>

          {!ready ? (
            <div className="flex items-center justify-center w-full h-[60vh]">
              <div className="h-8 w-8 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-label="Loading thread" />
            </div>
          ) : (
            <div className="relative w-full sm:w-auto max-w-full sm:max-w-4xl max-h-[85vh] overflow-hidden">
              <div className="overflow-y-auto overflow-x-hidden max-h-[85vh] p-2 sm:p-3">
                <ThreadTree
                  messages={threadedMessages}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                  onReply={handleReply}
                  onToggleLike={toggleLike}
                  onDelete={deleteMessage}
                  onEdit={editMessage}
                  loading={loading}
                  onAddComment={onAddComment}
                  alwaysExpanded={true}
                  hideActions={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
